import TextOptions from "%/dynamic-component-editor/bundles/text-options";
import { mapActions, mapWritableState } from "pinia";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import TextOption from "%/dynamic-component-editor/options/text-option";
import TextWrap from "%/dynamic-component-editor/options/text-wrap";
import HrefOption from "%/dynamic-component-editor/options/href-option";
import TextAlign from "%/dynamic-component-editor/options/text-align";
import Ellipsis from "%/dynamic-component-editor/options/ellipsis";
import TextDecoration from "%/dynamic-component-editor/options/text-decoration";
import Padding from "%/dynamic-component-editor/options/padding";
import Margins from "%/dynamic-component-editor/options/margins";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import FontFamily from "%/dynamic-component-editor/options/font-family";
import FontSize from "%/dynamic-component-editor/options/font-size";
import FontColor from "%/dynamic-component-editor/options/font-color";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import FontCase from "%/dynamic-component-editor/options/font-case";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import FontStyle from "%/dynamic-component-editor/options/font-style";
import LineHeightMultiplier from "%/dynamic-component-editor/options/line-height-multiplier";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import Label from "%/dynamic-component-editor/options/label";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import TextOverflow from "%/dynamic-component-editor/options/text-overflow";
import MaxVerticalLines from "%/dynamic-component-editor/options/max-vertical-lines";
import ClickAction from "%/dynamic-component-editor/options/click-action";
import TypographyBranding from "%/dynamic-component-editor/classes/branding/typography-branding";

const CLICK_BUFFER = 1000;
// Debounce the canvas inline-edit option write so the heavy compile/persist
// runs after the typing burst, keeping keystrokes instant.
const TEXT_WRITE_DEBOUNCE_MS = 300;

// FOR NOW BECAUSE OF THE MIXIN INHERITANCE I NEED TO CREATE DUPLICATES TO REMOVE CERTAIN
// BUNDLE OPTIONS FROM ELEMENTS
export const TextWithoutBrandingElementMixin = {
  elementPanelSettings: {
    displayIcon: "text",
    elementType: "element",
    elementGroup: "general",
  },
  brandingType: TypographyBranding,
  optionBundles: [
    AppearanceOptions,
    BasicTextOptions,
    TextOptions,
    new SpacingOptions({
      "default.padding": {
        "top": null,
        "left": null,
        "bottom": null,
        "right": null,
      },
    }),
    new BackgroundOptions({}, {
      "background-image": true,
    }),
    EffectsOptions,
  ],
  options: [
    ClickAction,
  ],
  data() {
    return {
      initialValue: "",
      TextOption,
      TextWrap,
      TextAlign,
      Ellipsis,
      FontCase,
      HrefOption,
      lastClicked: 0,
      editable: false,
      // Latches to true on the user's first inline-edit double-click and
      // never flips back. Builder text/button variants gate `<iRichText>`
      // on this flag so the heavy editor only mounts when someone starts
      // typing, then stays mounted across blur/refocus.
      hasStartedEditing: false,
      relatedElement: null,
      debounce: null,
      textWriteTimer: null,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  computed: {
    ...mapWritableState(useDynamicComponentEditorStore, [
      "dynamicComponentName",
      "isPage",
    ]),
    fontColor() {
      return this.getOptionValue(FontColor);
    },
    fontSizeNumberValue() {
      return parseInt(this.getOptionValue(FontSize).replace("px", ""));
    },
    fullTextOptionsAttributes() {
      return {
        width: this.myWidth,
        ...this.textOnlyAttributes,
        textStyleOverrides: this.textStyleOverrides,
      };
    },
    textOnlyAttributes() {
      return {
        maxVerticalLines: this.getOptionValue(MaxVerticalLines),
        overflow: this.getOptionValue(TextOverflow),
        wrap: this.getOptionValue(TextWrap),
        textAlign: this.getOptionValue(TextAlign),
        textCase: this.getOptionValue(FontCase),
        lineHeightMultiplier: this.getOptionValue(LineHeightMultiplier),
        textStyleOverrides: this.textOnlyStyleOverrides,
      };
    },
    // Shared font reads consumed by textOnlyStyleOverrides and textStyleOverrides.
    // Vue caches the computed; both consumers read it without re-running the
    // 8 underlying getOptionValue chain walks twice per render.
    commonFontReads() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily),
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER),
        standardFontWeight: this.getOptionValue(FontWeight),
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER),
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
      };
    },
    textOnlyStyleOverrides() {
      return {
        ...this.commonFontReads,
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor) || "standard",
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
      };
    },
    spacingStyleOverrides() {
      return {
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        // paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        // paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        // paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        // paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        // marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        // marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        // marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        // marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
      };
    },
    textStyleOverrides() {
      return {
        ...this.commonFontReads,
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor),
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER),
        ...this.spacingStyleOverrides,
        backgroundColor: this.getOptionValue(BackgroundColor),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
      };
    },
    textIsEmpty() {
      return this.initialValue.body_text === "\n" || !this.initialValue.body_text;
    },
    textOptionValue() {
      let textValue = this.getOptionValue(this.textValueOptionClass) || "";
      if (this.textValueKey && textValue) {
        // handle backwards compat
        if (this.textValueKey === "label") {
          if (!textValue.hasUpdated) {
            textValue = this.getOptionValue(Label);
          } else {
            textValue = textValue[this.textValueKey];
          }
        } else {
          textValue = textValue[this.textValueKey];
        }
      }

      return textValue;
    },
    previewMode() {
      // O(1) per-key Set membership instead of an O(N) global array scan.
      return !this.editable || !useDynamicComponentEditorStore().selectionSet.has(String(this.identifier));
    },
    // Plain-text projection of the persisted text-option body for the
    // pre-edit iText render. Same role as `text-element-mixin`'s
    // `initialValueText` — see the comment there.
    initialValueText() {
      if (!this.initialValue) {
        return "";
      }
      if (typeof this.initialValue === "string") {
        return this.initialValue;
      }
      if (this.initialValue.body_text === "\n") {
        return "";
      }
      if (typeof this.initialValue.body_text === "string") {
        return this.initialValue.body_text;
      }
      return "";
    },
    hasTextContent() {
      if (!this.initialValue) {
        return false;
      }
      if (typeof this.initialValue === "string") {
        return this.initialValue.length > 0;
      }
      return this.initialValue.body_text.length > 0 && this.initialValue.body_text !== "\n";
    },
    changeListMessage() {
      let message = `Element text value updated for ${this.dynamicComponentName}`;
      if (this.isPage) {
        message = `Element text value updated for the ${this.dynamicComponentName}`;
      }
      return message;
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
  },
  watch: {
    textOptionValue: {
      immediate: true,
      handler(newValue) {
        // While inline-editing, the editor owns its content; syncing the
        // round-tripped option value back would churn the contenteditable.
        if (this.editable) {
          return;
        }
        this.initialValue = newValue;
      },
    },
    previewMode(newValue) {
      if (newValue) {
        this.$refs.richTextElement.commands.closeBubbleMenu();
        useDynamicComponentEditorStore().removeElementFromEditingInline(this.identifier);
      }
    },
  },
  created() {
    // this.setDefaultBrandingValue();
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "findElementInLayout",
      "handleOnEdit",
    ]),
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    setDefaultBrandingValue() {
      if (!this.configuration.options["default.text-branding-style-option"]) {
        this.configuration.options["default.text-branding-style-option"] = "paragraph";
      }
    },
    handleBlur() {
      this.editable = false;
      // Flush any pending debounced write so leaving the field persists at once.
      if (this.textWriteTimer) {
        clearTimeout(this.textWriteTimer);
        this.textWriteTimer = null;
        this.commitTextOption(this.initialValue);
      }
    },
    handleEnter(event) {
      if (event.shiftKey) {
        this.handleBlur();
      }
    },
    handleClick(event) {
      if (this.editMode) {
        this.setElementToInspect(event);
        if (Date.now() - this.lastClicked < CLICK_BUFFER && !this.editable) {
          this.editable = true;
          this.hasStartedEditing = true;
          this.relatedElement = { ...this.dynamicComponentInstance.getElement(this.identifier) };
          useDynamicComponentEditorStore().setElementAsEditingInline(this.identifier);
          requestAnimationFrame(() => {
            const editableContent = this.$el.querySelector(".i-text.editor-content");
            if (editableContent) {
              editableContent.focus();

              const range = document.createRange();
              range.selectNodeContents(editableContent);

              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });
        }
        this.lastClicked = Date.now();
      }
    },
    setElementToInspect(event) {
      event.stopPropagation();
      this.handleOnEdit(this.identifier, event);
      useDynamicComponentEditorStore().setActiveBreakpointByAlias(this.breakpoint);
    },
    handleTextOptionUpdate(value) {
      if (!this.editMode) {
        return;
      }
      // Keep the editor's local model in lockstep (instant, no re-sync churn)
      // and debounce the heavy option write so typing stays responsive.
      this.initialValue = value;
      if (this.textWriteTimer) {
        clearTimeout(this.textWriteTimer);
      }
      this.textWriteTimer = setTimeout(() => {
        this.textWriteTimer = null;
        this.commitTextOption(value);
      }, TEXT_WRITE_DEBOUNCE_MS);
    },
    commitTextOption(value) {
      let valueToUpdate = value;

      if (this.textValueKey) {
        valueToUpdate = JSON.parse(JSON.stringify(this.getOptionValue(this.textValueOptionClass)));
        valueToUpdate[this.textValueKey] = value;
        valueToUpdate["hasUpdated"] = true;
      }
      const [state, breakpoint] = useDynamicComponentEditorStore().activeState.split(":");

      DynamicElement.get(this.identifier)?.updateOption(
        "text-option",
        breakpoint,
        state,
        valueToUpdate,
      );
    },
  },
};
